import { PageHeader } from '@lawpilots/design-system'
import { Box, Card } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PageLayout, ToolBar } from '@/common/components'
import { FEATURES } from '@/features.config'
import { CourseSearch, CourseSearchFormContext, courseSlice } from '@/features/course'
import { usePageVisited } from '@/hooks'
import type { MenusNavItem } from '@/common/types'
import { useTypedDispatch } from '@/store'
import CustomPalette from '@/theme/CustomPalette'
import { CourseList } from '../components/CourseList/CourseList'

type PageProps = {
  query?: string
}

const Courses = ({ ...rest }: PageProps): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  usePageVisited('Courses')
  const dispatch = useTypedDispatch()

  const openMenu = () => {
    dispatch(courseSlice.actions.openCreateCourseMenu(true))
  }

  const closeMenu = useCallback(() => {
    dispatch(courseSlice.actions.setExpandDialog(false))
    dispatch(courseSlice.actions.openCreateCourseMenu(false))
  }, [dispatch])

  const openUpdateMenu = (courseCode: string) => {
    dispatch(courseSlice.actions.openEditCourseMenu(true))
    dispatch(courseSlice.actions.setCourseMenu(courseCode))
  }

  const closeUpdateMenu = useCallback(() => {
    dispatch(courseSlice.actions.openEditCourseMenu(false))
  }, [dispatch])

  const courseMenus = useMemo<MenusNavItem[]>(
    () => [
      {
        index: '1',
        component: async () => {
          const module = await import(/* webpackChunkName: "CourseMenu" */ '@/features/course/menus/CourseMenu')
          return {
            default: module.CourseMenu,
          }
        },
        props: { title: t('create_course') },
      },
    ],
    [t],
  )

  const updateMenus = useMemo<MenusNavItem[]>(
    () => [
      {
        index: '1',
        component: async () => {
          const module = await import('@/features/course/menus/UpdateCourseMenu/UpdateCourseMenu')
          return {
            default: module.UpdateCourseMenu,
          }
        },
        props: { title: t('edit_course') },
      },
    ],
    [t],
  )

  const titles: string[] = [
    t('course_internal_reference'),
    t('catalog_course_title'),
    t('catalog_languages'),
    t('catalog_actions'),
  ]

  return (
    <PageLayout feature={FEATURES.COURSE}>
      <PageHeader title={t('courseCatalogue')} />
      <CourseSearchFormContext>
        <Card
          {...rest}
          sx={{
            border: `1px solid ${CustomPalette.SecondaryNightBlue200}`,
            borderRadius: '20px',
            boxShadow: 'none',
          }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <CourseSearch placeholder={t('search_courses_title_ID')} key={i18n.language} />
            <ToolBar
              sx={{ my: 3, mr: 3 }}
              withCustomButton
              customButtonLabel={t('create_course')}
              menus={courseMenus}
              openAction={openMenu}
              closeAction={closeMenu}
            />
          </Box>
          <CourseList
            routeLabel="courses"
            emptyList={t('noCourse')}
            loadingLabel={t('courses_loading')}
            titles={titles}
            menus={updateMenus}
            openAction={openUpdateMenu}
            closeAction={closeUpdateMenu}
          />
        </Card>
      </CourseSearchFormContext>
    </PageLayout>
  )
}

export default Courses
